import { Divider, Spin, Switch } from 'antd';
import classNames from 'classnames';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CANVAS_OBJECT_TYPE_PREFIX, UNIT_MEASURE } from '@common/constants';
import formatUOM from '@common/helpers/formatUOM';
import { DrawingShape, EdgeModel } from '@common/models';
import { COMING_SOON_IMAGE, getProductDescription } from '@common/utils';
import { formatPriceUnitOrFree } from '@common/utils/formatPriceUnitOrFree';
import { SectionSubtitle } from '@core/components';
import { useAnalyticsEvents } from '@core/hooks/useAnalyticsEvents';
import ImageUtils from '@core/utils/ImageUtils';
import { useSelectedCornerRadius } from '@ee-api/corner-radii-api/hooks/useSelectedCornerRadius';
import { useSelectedEdge } from '@ee-api/edges-api/hooks/useSelectedEdge';
import { useEdges } from '@ee-api/edges-api/useEdges';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { DrawingShapeContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { updateSelectedCornerRadius } from '@redux/slices/cornerRadiiSlice';
import { selectApplySameEdge, updateApplySameEdge, updateUserSelectedEdge } from '@redux/slices/edgeSlice';
import { selectFabricCanvas } from '@redux/slices/fabricSlice';
import { selectZipcode } from '@redux/slices/zipcodeSlice';

import drawingTool from '../design-countertop/drawer/drawingTool';

import './edges.scss';

export interface EdgeItemProps {
    edge: EdgeModel;
    onClick: (edge: EdgeModel, wasPreviouslySelected: boolean) => unknown;
    isSelected: boolean;
}

export interface EdgeRowProps {
    edgesArray: EdgeModel[];
}

const EdgeItem = ({ edge, onClick, isSelected }: EdgeItemProps) => {
    const description = getProductDescription(edge);
    const unitOfMeasure = formatUOM(edge.unitOfMeasure || UNIT_MEASURE.EDGE);

    return (
        <div
            className={classNames('card-option single-action-card', {
                selected: isSelected,
            })}
            onClick={() => onClick(edge, isSelected)}
        >
            <img
                alt="edge"
                className="card-option__image"
                src={edge.imageUrl ? ImageUtils.imgUrl(edge.imageUrl, 300) : COMING_SOON_IMAGE}
            />
            <div className="name">{description}</div>
            <div className="price">{formatPriceUnitOrFree(edge.price, unitOfMeasure)}</div>
        </div>
    );
};

const EdgesSwitch = ({ onChange }: { onChange: (checked: boolean) => unknown }) => {
    const { t } = useTranslation();
    const applySameEdge = useAppSelector(selectApplySameEdge);
    return (
        <div className="edges-switch">
            <div>{t('SCROLLABLE_AREA.EDGE.APPLY_SAME_EDGES')}</div>
            <Switch checked={applySameEdge} onChange={onChange} />
        </div>
    );
};

type Group<M, T> = { meta: M; items: T[] };
type EdgesGroup = Group<{ thickness?: string }, EdgeModel>;
const emptyArray = [] satisfies EdgesGroup[];

export function Edges(): JSX.Element {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const zipcode = useAppSelector(selectZipcode);
    const canvas = useAppSelector(selectFabricCanvas);
    const applySameEdge = useAppSelector(selectApplySameEdge);

    const { isFetching: isEdgesLoading, sortedEdges: edges } = useEdges();
    const { selectedValue: selectedEdge } = useSelectedEdge();
    const { selectedValue: selectedCornerRadius } = useSelectedCornerRadius();
    const { selectedValue: selectedMaterial } = useSelectedMaterial();

    const { drawingShapes, updateDrawingShapes, activeDrawingShape, areThereShapesInTheDrawingArea } =
        useContext(DrawingShapeContext);

    const { trackSelectEdge } = useAnalyticsEvents();

    const edgesGroupedByThickness: EdgesGroup[] = useMemo(() => {
        return edges
            .reduce((acc: EdgesGroup[], next) => {
                const groupIndex = acc.findIndex((group) => group.meta.thickness === next.thickness);
                if (groupIndex === -1) {
                    const newEdgesGroup: EdgesGroup = { meta: { thickness: next.thickness }, items: [next] };
                    return [...acc, newEdgesGroup];
                } else {
                    const updatedEdgesGroup: EdgesGroup = {
                        ...acc[groupIndex],
                        items: [...acc[groupIndex].items, next],
                    };
                    return acc.toSpliced(groupIndex, 1, updatedEdgesGroup);
                }
            }, emptyArray)
            .toSorted((a, b) => {
                const thicknessA = a.meta.thickness?.toLowerCase() ?? '';
                const thicknessB = b.meta.thickness?.toLowerCase() ?? '';

                if (thicknessA > thicknessB) {
                    return 1;
                } else if (thicknessA < thicknessB) {
                    return -1;
                } else {
                    return 0;
                }
            });
    }, [edges]);

    const selectManualEdge = (edge: EdgeModel) => {
        dispatch(updateUserSelectedEdge(edge));
        updateDrawingShapeEdge(edge);

        // remove corner radius circles and reset the selected corner status
        if (selectedCornerRadius && canvas) {
            drawingTool.removeObjectByName(canvas, CANVAS_OBJECT_TYPE_PREFIX.SELECTION_CIRCLE);
            dispatch(updateSelectedCornerRadius(selectedCornerRadius));
        }
    };

    const updateDrawingShapeEdge = (edge: EdgeModel, forceApplyAll = false) => {
        if (applySameEdge || forceApplyAll) {
            drawingShapes.forEach((dShape: DrawingShape) => {
                dShape.edge = edge;
            });
        } else {
            const foundShape = drawingShapes.find((dShape: DrawingShape) => dShape.id === activeDrawingShape?.id);
            if (foundShape) {
                foundShape.edge = edge;
            }
        }
        updateDrawingShapes([...drawingShapes]);
    };

    const onChangeSwitch = (checked: boolean) => {
        dispatch(updateApplySameEdge(checked));
        if (checked && selectedEdge?.productId) {
            updateDrawingShapeEdge(selectedEdge, true);
        }
    };

    /**
     * The callback action for selecting an edge card.  If it was previously selected, don't do anything new
     * @param edge
     * @param wasPreviouslySelected
     */
    const onEdgeClick = (edge: EdgeModel, wasPreviouslySelected: boolean) => {
        if (!wasPreviouslySelected) {
            trackSelectEdge({ edge });
        }
        selectManualEdge(edge);
    };

    return (
        <>
            {isEdgesLoading && <Spin style={{ fontSize: 24 }} />}
            {areThereShapesInTheDrawingArea && selectedMaterial && zipcode ? (
                <div id="edgeContent">
                    <SectionSubtitle
                        name={t('SCROLLABLE_AREA.EDGE.TITLE')}
                        description={t('SCROLLABLE_AREA.EDGE.DESCRIPTION')}
                        showLine
                        additionalComponent={<EdgesSwitch onChange={onChangeSwitch} />}
                    />
                    {edgesGroupedByThickness.map((edgeGroup) => {
                        return (
                            <div key={edgeGroup.meta.thickness}>
                                <div className="card-single-select">
                                    {edgeGroup.items.map((edge: EdgeModel, index: number) => (
                                        <EdgeItem
                                            key={index}
                                            edge={edge}
                                            isSelected={edge.productId === selectedEdge?.productId}
                                            onClick={onEdgeClick}
                                        />
                                    ))}
                                </div>
                                <Divider className="custom-card-divider hide-last-divider" />
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </>
    );
}
